/**
 * 导演模式共享样式 token（参考 EcommerceReplicate 页面的视觉语言）
 *
 * 设计原则：
 * - 浅色磨砂卡片：bg-white/85 + backdrop-blur-xl + 大圆角 + 极淡阴影
 * - 深色 hero 块：from-slate-950 via-slate-900 to-slate-800 渐变
 * - 主操作色：sky-500 / cyan-400 渐变
 * - 状态：emerald 完成 / rose 失败 / amber 处理中 / sky 排队
 *
 * 这些 token 通过 className 字符串复用，不引入额外运行时
 */

export const directorStyle = {
  // 页面级
  pageBg: 'bg-[#eef3f8]',
  pageBlur:
    'pointer-events-none fixed inset-0 overflow-hidden',

  // 卡片
  card:
    'rounded-[28px] border border-white/70 bg-white/85 shadow-[0_24px_80px_rgba(15,23,42,0.08)] backdrop-blur-xl',
  cardSm:
    'rounded-2xl border border-white/70 bg-white/85 shadow-[0_12px_40px_rgba(15,23,42,0.06)] backdrop-blur-xl',

  // 深色 hero 块
  hero:
    'rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white shadow-2xl shadow-slate-300/40',

  // 按钮
  btnPrimary:
    'inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-slate-300/70 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50',
  btnAccent:
    'inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-sky-500 to-cyan-400 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-sky-200/70 transition hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-50',
  btnGhost:
    'inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50',
  btnDanger:
    'inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-600 shadow-sm transition hover:bg-rose-50 disabled:opacity-50',

  // 标签 / 徽标
  badgeNeutral: 'rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600',
  badgeSky: 'rounded-full bg-sky-50 px-3 py-1 text-xs font-medium text-sky-700',
  badgeAmber: 'rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700',
  badgeEmerald: 'rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700',
  badgeRose: 'rounded-full bg-rose-50 px-3 py-1 text-xs font-medium text-rose-600',
  badgeViolet: 'rounded-full bg-violet-50 px-3 py-1 text-xs font-medium text-violet-700',

  // 输入
  input:
    'w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100',
  textarea:
    'w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100',

  // 字体
  label: 'text-xs font-medium uppercase tracking-[0.18em] text-slate-500',

  // 渐变进度条
  progressBg: 'h-1.5 overflow-hidden rounded-full bg-slate-100',
  progressFill: 'h-full rounded-full bg-gradient-to-r from-sky-500 to-cyan-400 transition-all',
};

/**
 * 项目状态徽标颜色映射
 */
export function getProjectStatusBadgeClass(
  status: 'draft' | 'producing' | 'ready' | 'composing' | 'completed' | 'archived'
): string {
  switch (status) {
    case 'draft':
      return directorStyle.badgeNeutral;
    case 'producing':
      return directorStyle.badgeSky;
    case 'ready':
      return directorStyle.badgeViolet;
    case 'composing':
      return directorStyle.badgeAmber;
    case 'completed':
      return directorStyle.badgeEmerald;
    case 'archived':
      return directorStyle.badgeNeutral;
    default:
      return directorStyle.badgeNeutral;
  }
}

/**
 * 视频任务状态徽标颜色映射
 */
export function getVideoStatusBadgeClass(
  status: 'none' | 'queued' | 'pending' | 'submitting' | 'processing' | 'completed' | 'failed' | 'timeout'
): string {
  switch (status) {
    case 'completed':
      return directorStyle.badgeEmerald;
    case 'failed':
    case 'timeout':
      return directorStyle.badgeRose;
    case 'queued':
      return directorStyle.badgeSky;
    case 'pending':
    case 'submitting':
    case 'processing':
      return directorStyle.badgeAmber;
    default:
      return directorStyle.badgeNeutral;
  }
}
